<template>
    <section class="tutorial-section" :id="item.moduleCode">
        <div class="tutorial-section__header">
            <h3 v-if="sectionTitle" class="tutorial-section__title">{{ sectionTitle }}</h3>
            <button v-if="fullVideoUrl" class="tutorial-section__play-full" type="button" @click="openFullModal">
                <span class="tutorial-section__play-full-icon">
                    <Play />
                </span>
                <span>{{ t('tutorial.playFullVideo') }}</span>
            </button>
        </div>

        <div class="tutorial-section__grid">
            <component v-for="(cell, idx) in videoList" :key="String(getCellId(cell, idx))"
                :is="resolveComponent(getDisplayMode(cell))" :item="cell" />
        </div>

        <TutorialVideoModal :visible="fullModalVisible" :src="fullVideoUrl" :title="sectionTitle" :content="fullContent"
            :chapters="fullChapters" @close="fullModalVisible = false" />
    </section>
</template>

<script setup lang="ts">
import { computed, ref, markRaw, onMounted, nextTick, type Component } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import type { Module } from '@/api/page_config'
import type { VideoChapter } from '@/stores/videoModal'
import { getStrWithRecord, CARD_COMPONENT_MAP } from '.'
import TutorialVideoHtml from './TutorialVideoHtml.vue'
import TutorialVideoModal from './TutorialVideoModal.vue'
import Play from '@/assets/icons/play.svg'

const props = defineProps<{
    item: Module
}>()

const { t } = useI18n()
const route = useRoute()
const fullModalVisible = ref(false)
const sectionInfo = computed(() => props.item.fieldData[0] || null)

/**
 * 页面加载时：若 URL hash 与当前 section 的 moduleCode 匹配，则滚动到该位置，
 * 然后重置 URL（移除 hash 锚点），避免后续 history 操作（如关闭弹窗）触发锚点跳转。
 */
onMounted(() => {
    const targetId = props.item.moduleCode
    if (!targetId || !route.hash) return
    if (route.hash !== `#${targetId}`) return

    nextTick(() => {
        const el = document.getElementById(targetId)
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' })
        }
        // 用 replaceState 移除锚点 hash，不触发 vue-router 导航与 scrollBehavior
        const fullPath = route.fullPath
        const hashIdx = fullPath.lastIndexOf('#')
        const cleanFullPath = hashIdx >= 0 ? fullPath.slice(0, hashIdx) : fullPath
        if (typeof window !== 'undefined') {
            history.replaceState(history.state, '', `#${cleanFullPath}`)
        }
    })
})

const sectionTitle = computed(() => props.item.mainTitle || '')
const fullVideoUrl = computed(() => getStrWithRecord(sectionInfo.value, 'video'))
const fullContent = computed(
    () => getStrWithRecord(sectionInfo.value, 'richText') || getStrWithRecord(sectionInfo.value, 'content') || ''
)
const fullChapters = computed<VideoChapter[]>(() => parseChapters(getStrWithRecord(sectionInfo.value, 'chapter')))

/** 视频卡片列表：items 为 TutorialVideoHtml 模块，实际卡片在其 fieldData 中，需打平 */
const videoList = computed(() => {
    const items = props.item.items || []
    return items.flatMap((item) => {
        const record = (item as unknown as Record<string, unknown>) || {}
        const fieldData = record.fieldData
        return Array.isArray(fieldData) ? fieldData : []
    })
})

/** 解析子项的 displayModeCode，缺失时回退到 TutorialVideoHtml */
function getDisplayMode(cell: unknown): string {
    const record = (cell as Record<string, unknown>) || {}
    return String(record.displayModeCode || '')
}

function resolveComponent(modeCode: string): Component {
    const comp = CARD_COMPONENT_MAP[modeCode]
    return comp ? markRaw(comp) : markRaw(TutorialVideoHtml)
}

function getCellId(cell: unknown, idx: number): string | number {
    const record = (cell as Record<string, unknown>) || {}
    return (record.id as string | number) ?? (record._id as string | number) ?? idx
}

function parseChapters(raw: unknown): VideoChapter[] {
    if (!raw) return []
    if (Array.isArray(raw)) {
        return raw
            .map((item) => {
                const r = (item as Record<string, unknown>) || {}
                const name = String(r.name || r.title || '')
                const time = Number(r.time ?? r.second ?? 0)
                return { name, time: Number.isFinite(time) ? time : 0 }
            })
            .filter((ch) => ch.name)
    }
    if (typeof raw === 'string') {
        try {
            return parseChapters(JSON.parse(raw))
        } catch {
            return []
        }
    }
    return []
}

function openFullModal(): void {
    if (!fullVideoUrl.value) return
    fullModalVisible.value = true
}
</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

$orange: #ff6d00;

.tutorial-section {
    width: 100%;
    margin-bottom: 2rem;
    /* 锚点滚动时为顶部 sticky 菜单栏留出空间，避免被遮挡 */
    scroll-margin-top: 56px;

    @media (min-width: $breakpoint-md) {
        margin-bottom: 2.5rem;
        scroll-margin-top: 64px;
    }

    &:last-child {
        margin-bottom: 0;
    }
}

.tutorial-section__header {
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;

    @media (min-width: $breakpoint-md) {
        margin-bottom: 1.25rem;
    }
}

.tutorial-section__title {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 700;
    color: $color-text;
    line-height: 1.3;

    @media (min-width: $breakpoint-md) {
        font-size: 1.25rem;
    }
}

.tutorial-section__play-full {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.875rem;
    border: 1px solid $orange;
    background: transparent;
    color: $orange;
    border-radius: 9999px;
    font-size: 0.8125rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
    white-space: nowrap;

    @media (min-width: $breakpoint-md) {
        font-size: $font-size-sm;
        padding: 0.4375rem 1rem;
    }

    &:hover {
        background: $orange;
        color: #fff;
    }
}

.tutorial-section__play-full-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 14px;
        height: 14px;
    }
}

.tutorial-section__grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;

    @media (min-width: $breakpoint-sm) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media (min-width: $breakpoint-lg) {
        grid-template-columns: repeat(5, 1fr);
        gap: 1.25rem;
    }
}
</style>
